import { ContactFormState } from '@/models/ContactFormState';
import { ContactSurSiteFormState } from '@/models/ContactSurSiteForm';
import { Delai } from '@/models/Delai';
import { DemandeInterventionContact } from '@/models/DemandeInterventionContact';
import { EquipementItems } from '@/models/EquipementItems';
import { EtatEquipement } from '@/models/EtatEquipement';
import { InterventionRequestResponse } from '@/models/InterventionRequestResponse';
import { Site } from '@/models/Site';
import { TypeItems } from '@/models/TypeItems';
import { store } from '@/plugins/store';
import { MutationTree } from 'vuex';
import { EQUIPEMENT, INTERVENTION } from './mutation-types';
import { CoordonneesAstreinte, DemandeInterventionState, initialState as resetState } from './state';
import { equipementState as equipementInitialState } from './modules/Equipement';

export const mutations: MutationTree<DemandeInterventionState> = {
    [INTERVENTION.SET_STEP](state, step: number) {
        state.step = step;
    },
    [INTERVENTION.SET_SITE](state, site: Site) {
        state.site = site;
    },
    [INTERVENTION.SET_SITES](state, sites: Site[]) {
        state.sites = sites;
    },
    [INTERVENTION.SET_TYPE](state, type: TypeItems) {
        state.type = type;
        state.delai = undefined;
    },
    [INTERVENTION.SET_DELAI](state, delai: Delai) {
        state.delai = delai;
    },
    [INTERVENTION.SET_EQUIPEMENT](state, equipement: EquipementItems | undefined | null) {
        state.equipement = equipement;
    },
    [INTERVENTION.SET_ETAT_EQUIPEMENT](state, etatEquipement: EtatEquipement) {
        state.etatEquipement = etatEquipement;
    },
    [INTERVENTION.SET_OBJET](state, objet: string) {
        state.objet = objet;
    },
    [INTERVENTION.SET_DESCRIPTIF](state, descriptif: string) {
        state.descriptif = descriptif;
    },
    [INTERVENTION.ADD_FILE](state, document: File) {
        state.documents = [...state.documents, document];
    },
    [INTERVENTION.REMOVE_FILE](state, document: File) {
        state.documents = state.documents.filter(f => f.name !== document.name);
    },
    [INTERVENTION.RESET_FILE](state) {
        state.documents = [];
    },
    [INTERVENTION.SET_DATE_AUTRE](state, date: string) {
        state.dateAutre = date;
    },
    [INTERVENTION.SET_SUIVI](state, suivi: boolean) {
        state.suivi = suivi;
    },
    [INTERVENTION.SET_RECURRENTE](state, recurrente: boolean) {
        state.recurrente = recurrente;
    },
    [INTERVENTION.SET_CONTACT_SUR_SITE](state, contactSurSite: ContactSurSiteFormState) {
        state.contactSurSite = contactSurSite;
    },
    [INTERVENTION.SET_CONTACT](state, contacts: DemandeInterventionContact[]) {
        state.contacts = contacts;
    },
    [INTERVENTION.RESET_INTERVENTION](state) {
        Object.assign(state, resetState);
    },
    [INTERVENTION.RESET_TO_STEP](state, step: number) {
        switch (step) {
            case 1:
                state.site = undefined;
                break;
            case 2:
                state.type = undefined;
                state.delai = undefined;
                state.dateAutre = '';
                break;
            case 3:
                store.commit(`equipementModule/${EQUIPEMENT.SET_EQUIPEMENT_FILTER}`, '');
                state.equipement = undefined;
                break;
        }
    },
    [INTERVENTION.SET_LOADING](state, isLoading: boolean) {
        state.isLoading = isLoading;
    },
    [INTERVENTION.SET_SUCCESS](state, success: boolean) {
        state.success = success;
    },
    [INTERVENTION.REMOVE_CONTACT](state, contact: ContactFormState) {
        state.listeContacts = [...state.listeContacts.filter(c => c.email !== contact.email)];
        if (state.contacts.length === 1) {
            state.contacts = state.listeContacts[0] ? [state.listeContacts[0]] : [];
        } else {
            state.contacts = [...state.contacts.filter(c => c.email !== contact.email)];
        }
    },
    [INTERVENTION.RESET_CONTACT](state) {
        state.listeContacts = [];
    },
    [INTERVENTION.REMOVE_CONTACT_SUR_SITE](state, contact: ContactSurSiteFormState) {
        state.listeContactSurSite = [...state.listeContactSurSite.filter(c =>
            c.telephoneMobile !== contact.telephoneMobile || c.telephoneFixe !== contact.telephoneFixe
        )];
        if (state.contactSurSite.telephoneMobile === contact.telephoneMobile &&
            state.contactSurSite.telephoneFixe === contact.telephoneFixe) {
            state.contactSurSite = state.listeContactSurSite[0] || {
                firstname: '',
                lastname: '',
                telephoneMobile: '',
                telephoneFixe: '',
                isConnectedUser: false,
            };
        }
    },
    [INTERVENTION.ADD_CONTACT](state, contact: ContactFormState) {
        state.listeContacts = [...state.listeContacts, contact];
        state.contacts = [...state.contacts, contact];
    },
    [INTERVENTION.ADD_CONTACT_SUR_SITE](state, contact: ContactSurSiteFormState) {
        state.listeContactSurSite = [...state.listeContactSurSite, contact];
        state.contactSurSite = contact;
    },
    [INTERVENTION.RESET_CONTACT_SUR_SITE](state) {
        state.listeContactSurSite = [];
    },
    [INTERVENTION.SET_RESPONSE](state, response: InterventionRequestResponse) {
        state.response = response;
    },
    [INTERVENTION.CLEAR_ALL](state) {
        state.contactSurSite = { firstname: '', lastname: '', telephoneMobile: '', telephoneFixe: '', isConnectedUser: false };
        state.contacts = [];
        state.dateAutre = '';
        state.delai = undefined;
        state.descriptif = '';
        state.documents = [];
        state.equipement = undefined;
        state.etatEquipement = undefined;
        state.isLoading = false;
        state.listeContactSurSite = [];
        state.listeContacts = [];
        state.objet = '';
        state.recurrente = false;
        state.site = undefined;
        state.step = 1;
        state.suivi = false;
        state.type = undefined;
        if(state.equipementModule){
            Object.assign(state.equipementModule, equipementInitialState());
        }
    },
    [INTERVENTION.SET_SOCIETE_ASTREINTE](state, societeAstreinte: CoordonneesAstreinte[]) {
        state.coordonneesAstreinte = societeAstreinte;
        state.isHeureAstreinte = true;
    },
    [INTERVENTION.ADD_SOCIETE_JOUR_FERIE](state, societeAstreinte: CoordonneesAstreinte[]) {
        state.coordonneesJourFerie = [...state.coordonneesJourFerie, ...societeAstreinte];
        state.isJourFerie = true;
    },
};
